import React, { Fragment, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Label,
  Input,
} from "reactstrap";
import { Breadcrumbs, H5 } from "../../AbstractElements";
import { getVenueById, deleteVenue } from "../../api/venue.api";
import { toast } from "react-toastify";
import { titleCase, sentenceCase } from "../../utils/case";
import { formatLeadTime } from "../../utils/leadTimeFormatter";

const VenueView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [venue, setVenue] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchVenue = async () => {
      try {
        const data = await getVenueById(id);
        setVenue(data);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchVenue();
  }, [id]);

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  if (!venue) {
    return <div className="text-center mt-5">Venue not found</div>;
  }

  const renderDeviceCard = (device) => (
    <Col className="project-box col-5 me-3" key={device.id}>
      <h6>{device.name}</h6>

      <p className="text-muted mb-3">
        {sentenceCase(
          t(device.type?.replace(/_/g, " "))
        )}
      </p>

      <p className="text-muted small mb-3">
        {titleCase(t("last synced"))}:{" "}
        {device.lastSyncedAt
          ? new Date(device.lastSyncedAt).toLocaleString()
          : titleCase(t("never"))}
      </p>

      <div className="d-flex align-items-center">
        <Label className="switch m-r-10 mb-0">
          <Input
            type="checkbox"
            checked={device.status === "on"}
            disabled
            readOnly
          />
          <span className="switch-state"></span>
        </Label>

        <Badge color={device.status === "on" ? "success" : "dark"}>
          {titleCase(t(device.status))}
        </Badge>
      </div>
    </Col>
  );

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("venue"))}
        title={titleCase(t("view venue"))}
        mainTitle={
          <div className="d-flex align-items-center gap-2">
            <span>{venue.name}</span>
            <Badge
              className="m-l-5"
              color={venue.status === "active" ? "success" : "dark"}
            >
              {titleCase(t(venue.status))}
            </Badge>
          </div>
        }
      />

      <Container fluid>
        <Row className="mb-4">
          <Col className="d-flex justify-content-end align-items-center gap-2 flex-wrap">

            <button
              className="btn btn-sm btn-light"
              onClick={() => scrollToSection("operating-hours")}
            >
              {titleCase(t("operating hours"))}
            </button>

            <button
              className="btn btn-sm btn-light"
              onClick={() => scrollToSection("remarks")}
            >
              {titleCase(t("remarks"))}
            </button>

            <button
              className="btn btn-sm btn-light"
              onClick={() => scrollToSection("devices")}
            >
              {titleCase(t("devices"))}
            </button>

            <div
              style={{
                width: "1px",
                height: "24px",
                backgroundColor: "#ddd",
                margin: "0 8px",
              }}
            />

            {/* Action buttons */}
            <button
              className="btn btn-sm btn-primary"
              onClick={() => navigate(`/venues/edit/${id}`)}
            >
              {titleCase(t("edit"))}
            </button>

            <button
              className="btn btn-sm btn-danger"
              disabled={deleting}
              onClick={async () => {
                const confirmed = window.confirm(
                  t("are you sure you want to delete this venue?")
                );

                if (!confirmed) return;

                try {
                  setDeleting(true);
                  await deleteVenue(id);
                  toast.success(t("deleted successfully"));
                  navigate("/venues");
                } catch (err) {
                  toast.error(err.message);
                } finally {
                  setDeleting(false);
                }
              }}
            >
              {deleting ? t("deleting...") : titleCase(t("delete"))}
            </button>

            <button
              className="btn btn-sm btn-dark"
              onClick={() => navigate(-1)}
            >
              {titleCase(t("back"))}
            </button>

          </Col>
        </Row>
        <Row className="mb-4">
          {/* ================= LEFT: IMAGE CARD ================= */}
          <Col md="4">
            <Card className="mb-4 shadow-sm h-100">
              <CardBody>
                {venue.photos ? (
                  <img
                    src={`https://bfiles.chinfumc.org.my/${venue.photos}`}
                    alt="venue"
                    style={{
                      width: "100%",
                      maxHeight: "350px",
                      objectFit: "contain",
                      borderRadius: "12px",
                    }}
                  />
                ) : (
                  <span className="text-muted">No image</span>
                )}

                {/* ✅ Description moved here */}
                <div className="mt-3">
                  <p className="text-muted">
                    {venue.description || "-"}
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* ================= MID: BOOKING CARD ================= */}
          <Col md="4">
            <Card className="mb-4 shadow-sm h-100" id="details">
              <CardBody>
                <Row>
                  {/* Allow Public Booking */}
                  <Col md="12" className="mb-3">
                    <div className="p-3 border rounded">
                      <Row className="align-items-center">
                        <Col md="4">
                          <p className="text-muted mb-0">
                            {titleCase(t("allow public booking"))}
                          </p>
                        </Col>
                        <Col md="8">
                          <Badge
                            color={venue.allowPublicBooking ? "success" : "dark"}
                          >
                            {venue.allowPublicBooking ? t("on") : t("off")}
                          </Badge>
                        </Col>
                      </Row>
                    </div>
                  </Col>

                  {/* Requires Approval */}
                  <Col md="12" className="mb-3">
                    <div className="p-3 border rounded">
                      <Row className="align-items-center">
                        <Col md="4">
                          <p className="text-muted mb-0">
                            {titleCase(t("requires approval"))}
                          </p>
                        </Col>
                        <Col md="8">
                          <Badge
                            color={venue.requiresApproval ? "success" : "dark"}
                          >
                            {venue.requiresApproval ? t("on") : t("off")}
                          </Badge>
                        </Col>
                      </Row>
                    </div>
                  </Col>

                  {/* Min Lead Time */}
                  <Col md="12" className="mb-3">
                    <div className="p-3 border rounded">
                      <Row className="align-items-center">
                        <Col md="4">
                          <p className="text-muted mb-0">
                            {titleCase(t("min lead time"))}
                          </p>
                        </Col>
                        <Col md="8">
                          <h6 className="mb-0">
                            {formatLeadTime(venue.minLeadTime, t)}
                          </h6>
                        </Col>
                      </Row>
                    </div>
                  </Col>

                  {/* Max Lead Time */}
                  <Col md="12" className="mb-3">
                    <div className="p-3 border rounded">
                      <Row className="align-items-center">
                        <Col md="4">
                          <p className="text-muted mb-0">
                            {titleCase(t("max lead time"))}
                          </p>
                        </Col>
                        <Col md="8">
                          <h6 className="mb-0">
                            {formatLeadTime(venue.maxLeadTime, t)}
                          </h6>
                        </Col>
                      </Row>
                    </div>
                  </Col>

                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* ================= RIGHT: PRICING CARD ================= */}
          <Col md="4">
            <Card className="mb-4 shadow-sm h-100" id="details">
              <CardBody>
                <Row>
                  {/* Requires Payment */}
                  <Col md="12" className="mb-3">
                    <div className="p-3 border rounded">
                      <Row className="align-items-center">
                        <Col md="4">
                          <p className="text-muted mb-0">
                            {titleCase(t("requires payment"))}
                          </p>
                        </Col>
                        <Col md="8">
                          <Badge
                            color={venue.requiresPayment ? "success" : "dark"}
                          >
                            {venue.requiresPayment ? t("on") : t("off")}
                          </Badge>
                        </Col>
                      </Row>
                    </div>
                  </Col>

                  {/* Default Cost */}
                  {venue.requiresPayment && (
                    <Col md="12" className="mb-3">
                      <div className="p-3 border rounded">
                        <Row className="align-items-center">
                          <Col md="4">
                            <p className="text-muted mb-0">
                              {titleCase(t("default cost"))}
                            </p>
                          </Col>
                          <Col md="8">
                            <h6 className="mb-0">RM {venue.cost}</h6>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  )}

                  {/* Peak Cost */}
                  {venue.requiresPayment && (
                    <Col md="12" className="mb-3">
                      <div className="p-3 border rounded">
                        <Row className="align-items-center">
                          <Col md="4">
                            <p className="text-muted mb-0">
                              {titleCase(t("peak cost"))}
                            </p>
                          </Col>
                          <Col md="8">
                            <h6 className="mb-0">{venue.peakCost ? `RM ${venue.peakCost}` : "-"}</h6>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  )}

                  {/* Charged By */}
                  {venue.requiresPayment && (
                    <Col md="12" className="mb-3">
                      <div className="p-3 border rounded">
                        <Row className="align-items-center">
                          <Col md="4">
                            <p className="text-muted mb-0">
                              {titleCase(t("charged by"))}
                            </p>
                          </Col>
                          <Col md="8">
                            <h6 className="mb-0">
                              {titleCase(venue.chargedBy || "-")}
                            </h6>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  )}
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col className="col-6">
            {/* ================= OPERATING HOURS ================= */}
            <Card className="mb-4 shadow-sm" id="operating-hours">
              <CardHeader>
                <H5 className="mb-0">
                  {titleCase(t("operating hours"))}
                </H5>
              </CardHeader>

              <CardBody>
                {venue.operatingHours &&
                venue.operatingHours.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-bordered mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{titleCase(t("day"))}</th>
                          <th>{titleCase(t("start time"))}</th>
                          <th>{titleCase(t("end time"))}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {venue.operatingHours.map((oh, index) => (
                          <tr key={index}>
                            <td>{titleCase(t(oh.day.toLowerCase()))}</td>
                            <td>{oh.startTime}</td>
                            <td>{oh.endTime}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="mb-0 text-muted">-</p>
                )}
              </CardBody>
            </Card>
            {/* ================= REMARKS ================= */}
            <Card className="mb-4 shadow-sm" id="remarks">
              <CardHeader>
                <H5 className="mb-0">
                  {titleCase(t("remarks"))}
                </H5>
              </CardHeader>

              <CardBody>
                {venue.remark ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: venue.remark }}
                  />
                ) : (
                  <p className="mb-0 text-muted">-</p>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col className="col-6" id="devices">
            {/* ================= DEVICE CONNECTIONS ================= */}
            <Card className="mb-4 shadow-sm">
              <CardHeader>
                <H5 className="mb-0">
                  {titleCase(t("device connection"))}
                </H5>
              </CardHeader>
              <CardBody>
                {venue.devices && venue.devices.length > 0 ? (
                  <Row>
                    {venue.devices.map((device) =>
                      renderDeviceCard(device)
                    )}
                  </Row>
                ) : (
                  <p className="mb-0 text-muted">-</p>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>


        <Row>
        </Row>

        {/* ================= ACTIONS ================= */}
        <div className="d-flex justify-content-end gap-2 mb-4">
        </div>

        {showScrollTop && (
          <button
            className="btn-primary"
            onClick={scrollToTop}
            style={{
              position: "fixed",
              bottom: "30px",
              right: "30px",
              zIndex: 999,
              borderRadius: "50%",
              width: "45px",
              height: "45px",
              border: "none",
              color: "#fff",
              boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
              cursor: "pointer",
            }}
            title={titleCase(t("scroll to top"))}
          >
            ↑
          </button>
        )}
      </Container>
    </Fragment>
  );
};

export default VenueView;